import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  TablePagination,
  Grid,
  Typography,
} from '@mui/material';
import {
  Search,
  FilterList,
  FileDownload,
  Person,
} from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { apiService } from '../../services/apiService';
import { useAppSelector } from '../../store/hooks';
import { useNavigate } from 'react-router-dom';
import SettingsSidebar from '../../components/Common/SettingsSidebar';

interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  avatar?: string;
  status: 'active' | 'inactive' | 'suspended';
  department?: string;
  position?: string;
  hire_date?: string;
  salary?: number;
  roles: Array<{
    id: number;
    name: string;
    display_name: string;
    is_primary: boolean;
  }>;
  primary_role?: {
    id: number;
    name: string;
    display_name: string;
    description: string;
  };
  created_at: string;
  last_login_at?: string;
}

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  color?: string;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
}

const Users: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch data from API
  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers();
      setUsers(response.data.data || response.data);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        // Token expired or invalid, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        toast.error(error.message || 'Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getUserPrimaryRole = (user: User) => {
    // First try to get primary_role from the API response
    if ((user as any).primary_role) {
      return (user as any).primary_role.display_name || (user as any).primary_role.name || 'No Role';
    }
    
    // Fallback to checking roles array
    if (user.roles && user.roles.length > 0) {
      const primaryRole = user.roles.find(role => role.is_primary);
      if (primaryRole) {
        return primaryRole.display_name || primaryRole.name || 'No Role';
      }
      
      // If no primary role found, use the first role
      const firstRole = user.roles[0];
      return firstRole.display_name || firstRole.name || 'No Role';
    }
    
    return 'No Role';
  };

  const handleDeleteUser = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    const userName = user ? user.name : 'this user';
    
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${userName}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await apiService.deleteUser(userId);
        
        Swal.fire({
          title: 'Deleted!',
          text: 'User has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
        });
        
        fetchUsers(); // Refresh the users list
      } catch (error: any) {
        console.error('Error deleting user:', error);
        
        Swal.fire({
          title: 'Error!',
          text: error.message || 'Failed to delete user',
          icon: 'error',
          confirmButtonColor: '#3085d6',
        });
      }
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.phone && user.phone.includes(searchTerm)) ||
    getUserPrimaryRole(user).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<Person />}
          onClick={() => navigate('/users/add')}
          sx={{ 
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            backgroundColor: '#4A90E2',
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#3b82f6',
            },
          }}
        >
          New Users
        </Button>
      </Box>

     

      <Grid container spacing={3}>
        {/* Left Sidebar - Settings Menu */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>

          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Total Users
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {users.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    Active Users
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {users.filter(user => user.status === 'active').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                borderRadius: '16px',
                backgroundColor: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em'
                  }}>
                    New Users (This Month)
                  </Typography>
                  <Typography variant="h2" sx={{ 
                    color: '#1e40af', 
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em'
                  }}>
                    {users.filter(user => {
                      const userDate = new Date(user.created_at);
                      const currentDate = new Date();
                      return userDate.getMonth() === currentDate.getMonth() && 
                             userDate.getFullYear() === currentDate.getFullYear();
                    }).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Search and Action Bar */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    placeholder="Search here"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#4A90E2' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      width: 400,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#e0f2fe',
                        borderColor: '#e0f2fe',
                        '& fieldset': {
                          borderColor: '#e0f2fe',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bae6fd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4A90E2',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: '#374151',
                        '&::placeholder': {
                          color: '#9ca3af',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    sx={{ 
                      px: 3,
                      py: 1.5,
                      borderRadius: '12px',
                      borderColor: '#e0f2fe',
                      backgroundColor: 'white',
                      color: '#4A90E2',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#bae6fd',
                        backgroundColor: '#f0f9ff',
                      },
                    }}
                  >
                    Filter
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  sx={{ 
                    px: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    backgroundColor: '#4A90E2',
                    color: 'white',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#3b82f6',
                    },
                  }}
                >
                  Export
                </Button>
              </Box>

              {/* Data Table */}
              <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e5e7eb', height: 'calc(100vh - 450px)' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      <TableCell padding="checkbox">
                        <Checkbox />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>User Full Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Email Address</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Phone Number</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Loading users...
                        </TableCell>
                      </TableRow>
                    ) : filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((user) => (
                        <TableRow key={user.id} hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                          <TableCell padding="checkbox">
                            <Checkbox />
                          </TableCell>
                          <TableCell sx={{ color: '#374151' }}>{user.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={getUserPrimaryRole(user)}
                              size="small"
                              sx={{
                                backgroundColor: '#e0f2f1',
                                color: '#0d9488',
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#374151' }}>{user.email}</TableCell>
                          <TableCell sx={{ color: '#374151' }}>{user.phone || 'N/A'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => navigate(`/users/edit/${user.id}`)}
                                sx={{
                                  backgroundColor: '#e0f2f1',
                                  color: '#0d9488',
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  '&:hover': {
                                    backgroundColor: '#ccedea',
                                  },
                                }}
                              >
                                Update
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleDeleteUser(user.id)}
                                sx={{
                                  backgroundColor: '#fee2e2',
                                  color: '#dc2626',
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  '&:hover': {
                                    backgroundColor: '#fecaca',
                                  },
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Showing"
                labelDisplayedRows={({ from, to, count }) =>
                  `Showing ${from} to ${to} out of ${count} records`
                }
                sx={{
                  '& .MuiTablePagination-selectLabel': {
                    color: '#6b7280',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    color: '#6b7280',
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
};

export default Users;